import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BigButton } from "../components/BigButton";
import { LanguagePicker } from "../components/LanguagePicker";
import { claimDevice } from "../lib/device-auth";
import { KioskApiError } from "../lib/kiosk-client";

interface Props {
  onProvisioned: () => void;
}

export function SetupPage({ onProvisioned }: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await claimDevice(code.trim());
      onProvisioned();
    } catch (err) {
      if (
        err instanceof KioskApiError &&
        (err.status === 404 || err.status === 409)
      ) {
        setError(t("setup.errorInvalid"));
      } else {
        setError(t("setup.errorGeneric"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="flex flex-col items-center justify-center h-full gap-8 p-12 bg-[var(--kiosk-brand-bg)]"
      data-testid="setup-page"
    >
      <div className="absolute top-6 right-6">
        <LanguagePicker />
      </div>
      <h1 className="text-5xl font-bold text-[var(--kiosk-brand-primary)]">
        {t("setup.title")}
      </h1>
      <p className="text-xl text-[var(--kiosk-brand-muted)] text-center max-w-md">
        {t("setup.subtitle")}
      </p>
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-6 w-full max-w-md"
        noValidate
      >
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t("setup.codePlaceholder")}
          className="w-full rounded-[var(--kiosk-brand-radius)] border-2 border-[var(--kiosk-brand-primary)] px-6 py-4 text-2xl focus:outline-none focus:border-[var(--kiosk-brand-accent)]"
          aria-label={t("setup.codePlaceholder")}
          autoFocus
          autoComplete="off"
          autoCapitalize="characters"
          maxLength={32}
          disabled={submitting}
        />
        {error && (
          <p role="alert" className="text-red-600 text-lg text-center">
            {error}
          </p>
        )}
        <BigButton
          type="submit"
          variant="primary"
          fullWidth
          disabled={!code.trim() || submitting}
        >
          {submitting ? t("setup.submitting") : t("setup.submit")}
        </BigButton>
      </form>
    </div>
  );
}
